import quickAddToCart from '../../sections/Cart/quickAddToCart';
import { quickShopClickTracking, quickShopSizeChangeTracking } from '../GTMEvents';
import { trackConversion, getConstructorPrice } from '../../utils/ConstructorTracking';

export default class QuickAdd {
  static #instances = [];
  // Check your origin session, if you are Domestic or International
  static isIntl = window.Shopify?.country !== 'US';

  #isExpanded = false;
  #isRendered = false;
  #isLoading = false;

  /**
   * Creates a QuickAdd instance
   * @param {Object} variants - Product variants
   * @param {Object} options - Product options
   * @param {String} location - The location of the product card
   */
  constructor({ variants, options, productUrl, productId, title, location }) {
    this.variants = variants;
    this.options = options;
    this.currentVariantId = variants[0].id;
    this.currentColor = options?.[0]?.color || null;
    this.availableSizes = [];
    this.location = location || '';

    // Configuration
    this.successMessageDuration = 5000;
    this.productUrl = productUrl;
    this.productId = productId;
    this.title = title;

    // DOM references
    this.node = document.createElement('div');
    this.buttonNode = null;
    this.sizesPodNode = null;
    this.liveRegionNode = null;
    this.sizesPodId = `quick-add-sizes-${Math.random().toString(36).substr(2, 9)}`;

    // Register this instance
    QuickAdd.#instances.push(this);

    this.init();
  }

  /**
   * Close all open size pickers except the specified instance
   * @param {QuickAdd} exceptInstance - Instance to keep open
   */
  static #closeAllExcept(exceptInstance) {
    QuickAdd.#instances.forEach((instance) => {
      if (instance !== exceptInstance && instance.#isExpanded) {
        instance.closeSizePicker();
      }
    });
  }

  /**
   * Initialize the QuickAdd component
   */
  init() {
    this.node.classList.add('product-card-c-quick-add');

    // Build DOM structure
    this.#buildButton();
    this.#buildSizePod();
    this.#buildLiveRegion();

    // Setup event handlers
    this.#setupEventHandlers();
  }

  /**
   * Public method called by ProductCard when color swatch changes
   * @param {String} variantId - The new variant ID
   */
  updateForVariant(variantId) {
    this.currentVariantId = variantId;

    // Find the color for this variant
    const variant = this.variants.find((v) => (v.data.variation_id || v.data.id || v.id) === variantId);
    if (variant) {
      this.currentColor = variant.data.color;
    }

    // Rebuild size picker if already rendered
    if (this.#isRendered && this.sizesPodNode) {
      const wasExpanded = this.#isExpanded;

      if (wasExpanded) {
        // Close first, then rebuild after transition completes
        this.closeSizePicker();
        this.sizesPodNode.addEventListener(
          'transitionend',
          () => {
            this.sizesPodNode.innerHTML = '';
            this.#isRendered = false;
            this.#buildSizePicker();
          },
          { once: true },
        );
      } else {
        // Not visible, rebuild immediately
        this.sizesPodNode.innerHTML = '';
        this.#isRendered = false;
        this.#isExpanded = false;
        this.#buildSizePicker();
      }
    }
  }

  /**
   * Build the Quick Add button
   */
  #buildButton() {
    this.buttonNode = document.createElement('button');
    this.buttonNode.classList.add('product-card-c-quick-add__button');
    this.buttonNode.type = 'button';
    this.buttonNode.setAttribute('aria-label', 'Quick Add');
    this.buttonNode.setAttribute('aria-expanded', 'false');
    this.buttonNode.setAttribute('aria-controls', this.sizesPodId);
    this.buttonNode.setAttribute('data-cnstrc-btn', 'add_to_cart');
    this.buttonNode.setAttribute('data-location', this.location);

    const buttonText = document.createElement('span');
    buttonText.classList.add('product-card-c-quick-add__button-text');
    buttonText.textContent = 'QUICK ADD';

    const buttonIcon = document.createElement('span');
    buttonIcon.classList.add('product-card-c-quick-add__button-icon');
    buttonIcon.innerHTML =
      '<svg width="12" height="12" fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"><path d="M6 2.25v7.5M9.75 6h-7.5"/></svg>';

    this.buttonNode.appendChild(buttonIcon);
    this.buttonNode.appendChild(buttonText);
    this.node.appendChild(this.buttonNode);
  }

  /**
   * Build the size selection pod
   */
  #buildSizePod() {
    const wrapper = document.createElement('ul');
    wrapper.classList.add('product-card-c-quick-add__sizes');
    wrapper.id = this.sizesPodId;
    this.sizesPodNode = wrapper;
    this.node.prepend(this.sizesPodNode);
  }

  /**
   * Build the live region for add-to-cart feedback
   */
  #buildLiveRegion() {
    this.liveRegionNode = document.createElement('span');
    this.liveRegionNode.setAttribute('role', 'status');
    this.liveRegionNode.setAttribute('aria-live', 'polite');
    this.liveRegionNode.classList.add('visually-hidden');
    this.node.appendChild(this.liveRegionNode);
  }

  /**
   * Build the size picker dropdown
   */
  #buildSizePicker() {
    this.#isRendered = true;

    const setOfVariants = this.variants
      .filter((variant) => variant.data.color === this.currentColor)
      .sort((a, b) => a.data.variant_position - b.data.variant_position);

    if (!setOfVariants.length) return;

    setOfVariants.forEach((variant) => {
      const variantId = variant.data.variation_id || variant.data.id || variant.id;
      const isAvailable = QuickAdd.isIntl
        ? Number(variant.data.inventory_quantity_international) > 0
        : Number(variant.data.inventory || variant.data.inventoryQuantity) > 0;
      const sizeItem = document.createElement('li');
      const sizeBtn = document.createElement('button');
      sizeBtn.classList.add('product-card-c-quick-add__size-item');
      sizeBtn.setAttribute('data-variant-id', variantId);
      sizeBtn.type = 'button';

      sizeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.target.classList.contains('--sold-out')) {
          window.location = `${this.productUrl}?variant=${variantId}#waitlist`;
          return;
        }
        this.#addToCart(variantId, variant.data.size);
      });

      if (!isAvailable) {
        sizeBtn.classList.add('--sold-out');
        sizeBtn.setAttribute('aria-disabled', 'true');

        const size = document.createElement('span');
        size.innerHTML = `<span>${variant.data.size}</span>
          <svg
            width="10"
            height="8"
            fill="none"
          >
            <path
              fill="#92959B"
              d="M1 0C.449 0 0 .449 0 1v6c0 .551.449 1 1 1h8c.552 0 1-.449 1-1V1c0-.551-.448-1-1-1H1Zm0 1h8v.003L5 3.5 1 1.002V1Zm0 1.002L5 4.5l4-2.497L9.001 7H1V2.002Z"
            />
          </svg>`;
        size.style.display = 'flex';
        size.style.alignItems = 'center';
        size.style.gap = '3px';
        size.style.color = '#92959B';
        sizeBtn.appendChild(size);
      } else {
        sizeBtn.textContent = variant.data.size;
      }

      sizeItem.appendChild(sizeBtn);
      this.sizesPodNode.appendChild(sizeItem);
    });
  }

  /**
   * Setup all event handlers
   */
  #setupEventHandlers() {
    // Button click to toggle size picker
    this.buttonNode.addEventListener('click', (e) => this.#handleButtonClick(e));

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (this.#isExpanded && !this.node.contains(e.target)) {
        this.closeSizePicker();
      }
    });

    // Escape key to close
    this.node.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#isExpanded) {
        this.closeSizePicker();
      }
    });
  }

  /**
   * Handle button click - toggle size picker or add directly for single size
   * @param {MouseEvent} e
   */
  #handleButtonClick(e) {
    e.preventDefault();
    e.stopPropagation();

    !this.#isExpanded && !this.#isRendered && this.#buildSizePicker();
    this.#toggleSizePicker();
    setTimeout(() => quickShopClickTracking(this.title, this.productId), 0);
  }

  /**
   * Toggle size picker visibility
   */
  #toggleSizePicker() {
    if (this.#isExpanded) {
      // this.sizesPodNode.remove();
      this.closeSizePicker();
    } else {
      // Close all other open pickers before opening this one
      QuickAdd.#closeAllExcept(this);
      this.#openSizePicker();
    }
  }

  /**
   * Open size picker
   */
  #openSizePicker() {
    this.#isExpanded = true;
    this.sizesPodNode.classList.add('is-expanded');
    this.sizesPodNode.setAttribute('aria-hidden', 'false');
    this.buttonNode.setAttribute('aria-expanded', 'true');
  }

  /**
   * Close size picker
   */
  closeSizePicker() {
    this.#isExpanded = false;
    this.sizesPodNode.classList.remove('is-expanded');
    this.sizesPodNode.setAttribute('aria-hidden', 'true');
    this.buttonNode.setAttribute('aria-expanded', 'false');
  }

  /**
   * Add item to cart
   * @param {String} variantId - The variant ID to add
   */
  async #addToCart(variantId, size) {
    if (this.#isLoading) return;

    this.#setLoadingState(true);

    // Get all Quick Add buttons and disable them
    const allQuickAddButtons = document.querySelectorAll('.product-card-c-quick-add__button');
    allQuickAddButtons.forEach((btn) => (btn.disabled = true));

    // Get text and icon spans
    const textSpan = this.buttonNode.querySelector('.product-card-c-quick-add__button-text');
    const iconSpan = this.buttonNode.querySelector('.product-card-c-quick-add__button-icon');

    // Store originals
    const originalText = textSpan.textContent;
    const originalIcon = iconSpan.innerHTML;

    // Set loading state - just show spinner, hide text
    textSpan.style.display = 'none';
    iconSpan.innerHTML = '<span class="product-card-c-quick-add__spinner"></span>';

    try {
      this.closeSizePicker();
      await quickAddToCart({ variantId, productName: this.title, productId: this.productId, location: this.location });

      const variant = this.variants.find((v) => (v.data.variation_id || v.data.id || v.id) === variantId);
      const revenue = variant?.data ? getConstructorPrice(variant.data) : undefined;
      const searchContainer = this.node.closest('[data-cnstrc-search]');
      const term = searchContainer
        ? new URLSearchParams(window.location.search).get('q') ||
          document.querySelector('[data-cnstrc-search-input]')?.value ||
          ''
        : undefined;
      const resultId = this.node.closest('[data-cnstrc-result-id]')?.dataset.cnstrcResultId || undefined;

      trackConversion(term, {
        itemId: this.productId,
        revenue,
        itemName: this.title,
        variationId: variantId,
        resultId,
        type: 'add_to_cart',
      });

      // Success state
      textSpan.style.display = '';
      textSpan.textContent = 'Added to bag!';
      iconSpan.innerHTML =
        '<svg width="16" height="16" fill="#fff"><path d="M13.529 3.529 6 11.057 3.138 8.195l-.943.943L6 12.943l8.471-8.472-.942-.942Z" /></svg>';
      this.buttonNode.classList.add('product-card-c-quick-add__button--is-successful');
      this.liveRegionNode.textContent = 'Added to cart';

      // Re-enable other buttons immediately so user can add from other cards
      allQuickAddButtons.forEach((btn) => {
        if (btn !== this.buttonNode) btn.disabled = false;
      });

      // Revert this button after success message duration
      setTimeout(() => {
        textSpan.textContent = originalText;
        iconSpan.innerHTML = originalIcon;
        this.buttonNode.classList.remove('product-card-c-quick-add__button--is-successful');
        this.buttonNode.disabled = false;
        this.#setLoadingState(false);
        this.liveRegionNode.textContent = '';
        quickShopSizeChangeTracking(size);
      }, this.successMessageDuration);
    } catch (error) {
      console.error('QuickAdd error:', error);
      this.closeSizePicker();
      // On error, restore immediately
      textSpan.style.display = '';
      textSpan.textContent = originalText;
      iconSpan.innerHTML = originalIcon;
      allQuickAddButtons.forEach((btn) => (btn.disabled = false));
      this.#setLoadingState(false);
      this.liveRegionNode.textContent = 'Error adding to cart';
    }
  }

  /**
   * Set loading state
   * @param {Boolean} loading
   */
  #setLoadingState(loading) {
    this.#isLoading = loading;
  }
}
